/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { compact } from 'lodash';
import { DateTime } from 'luxon';
import { convertToISOString } from 'blb-utils/date';

export const ClosingScheduleStatus = {
  // The closing schedule was completed and is currently active.
  ACTIVE: 'ACTIVE',

  // The schedule is confirmed after ops / the settlement agent confirms all information.
  CONFIRMED: 'CONFIRMED',

  // The closing schedule is still being updated by the borrower.
  IN_PROGRESS: 'IN_PROGRESS',

  // This closing schedule was voided, usually due to a change in closing date.
  INACTIVE: 'INACTIVE',
};

export const ClosingMethods = {
  IN_OFFICE: 'IN_OFFICE',
  MOBILE: 'MOBILE',
  ONLINE: 'ONLINE',
};

export const ClosingMethodDescriptionMapping = {
  IN_OFFICE: 'in office',
  MOBILE: 'mobile',
  ONLINE: 'online',
};

export const TimeSlotToDescriptionMapping = {
  SEVEN_TO_EIGHT_AM: '7–8 AM',
  EIGHT_TO_NINE_AM: '8–9 AM',
  ELEVEN_TO_TWELVE_AM: '11–12 AM',
  ELEVEN_TO_TWELVE_PM: '11–12 PM',
  FIVE_TO_SIX_PM: '5–6 PM',
  FOUR_TO_FIVE_PM: '4–5 PM',
  NINE_TO_TEN_AM: '9–10 AM',
  ONE_TO_TWO_PM: '1–2 PM',
  SIX_TO_SEVEN_PM: '6–7 PM',
  TEN_TO_ELEVEN_AM: '10–11 AM',
  THREE_TO_FOUR_PM: '3–4 PM',
  TWELVE_TO_ONE_PM: '12–1 PM',
  TWO_TO_THREE_PM: '2–3 PM',
  SEVEN_TO_EIGHT_PM: '7–8 PM',
  EIGHT_TO_NINE_PM: '8-9 PM',
  NINE_TO_TEN_PM: '9-10 PM',
  TEN_TO_ELEVEN_PM: '10-11 PM',
};

export const TimezoneEnumToShortTimezoneMappings: Record<ClosingSchedule.Timezone, string> = {
  ALASKA_TIME: 'AT',
  CENTRAL_TIME: 'CT',
  EASTERN_TIME: 'ET',
  HAWAII_TIME: 'HST',
  MOUNTAIN_TIME_ARIZONA: 'MST',
  MOUNTAIN_TIME_DENVER: 'MT',
  PACIFIC_TIME: 'PT',
  HAWAII_ALEUTIAN_TIME: 'HT',
  WAKE_ISLAND_TIME: 'WAKT',
  GUAM_TIME: 'ChST',
  SAMOA_TIME: 'SST',
  PUERTO_RICO_TIME: 'AST',
};

const RONSystemNameMapping = {
  BLEND_SIGNING_ROOM: 'Blend Signing Room',
  NOTARYCAM: 'NotaryCam',
};

export const generateTimePreferenceString = (slots: ClosingSchedule.TimeSlot[]): string => {
  let preferredSlots = '';

  if (!slots) {
    return '';
  }

  for (let slot = 0; slot < slots.length; slot++) {
    preferredSlots += TimeSlotToDescriptionMapping[slots[slot]];
    if (slot !== slots.length - 1) {
      preferredSlots += ', ';
    }
  }

  return preferredSlots;
};

export const generateBorrowerPreferredLocationString = (location: ClosingSchedule.Address): string => {
  const stateZipCode = compact([location?.state, location?.zipCode]).join(' ');
  return compact([location?.addressLineOne, location?.addressLineTwo, location?.city, stateZipCode]).join(', ');
};

export const generatePreferredLocationString = (location: ClosingSchedule.Address | null): string => {
  return compact([
    location?.addressLineOne,
    location?.addressLineTwo,
    location?.city,
    compact([location?.state, location?.zipCode]).join(' '),
  ]).join(', ');
};

export const locationLexeme = (closingMethod: string): string =>
  closingMethod === ClosingMethods.IN_OFFICE ? 'near' : 'at';

export const generateNotaryName = (schedule: EClose.API.ScheduleResponse): string => {
  return compact([schedule.notaryFirstName, schedule.notaryLastName]).join(' ');
};

export const generateAppointmentMethodString = (method: ClosingSchedule.ClosingMethod): string => {
  return ClosingMethodDescriptionMapping[method];
};

export const getTimezone = (timezone: ClosingSchedule.Timezone): string => {
  return TimezoneEnumToShortTimezoneMappings[timezone];
};

export const getRonTransactionPlatformName = (closing: EClose.FullClosing): string => {
  if (closing.ronTransactions === undefined || closing.ronTransactions.length < 1) {
    return 'No Platform Specified';
  }
  const systemName = closing.ronTransactions[0].systemName;

  return RONSystemNameMapping[systemName];
};

/**
 * Value must be a real TZ name: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
 */
export const TimezoneEnumToTimezoneMapping: Record<ClosingSchedule.Timezone, string> = {
  PACIFIC_TIME: 'America/Los_Angeles',
  EASTERN_TIME: 'America/New_York',
  CENTRAL_TIME: 'America/Chicago',
  MOUNTAIN_TIME_DENVER: 'America/Denver',
  MOUNTAIN_TIME_ARIZONA: 'America/Phoenix',
  ALASKA_TIME: 'America/Anchorage',
  HAWAII_TIME: 'Pacific/Honolulu',
  HAWAII_ALEUTIAN_TIME: 'America/Adak',
  WAKE_ISLAND_TIME: 'Pacific/Wake',
  GUAM_TIME: 'Pacific/Guam',
  SAMOA_TIME: 'Pacific/Pago_Pago',
  PUERTO_RICO_TIME: 'America/Puerto_Rico',
};

export const getScheduledDateTimeText = (schedule: EClose.API.ScheduleResponse): string | null => {
  const scheduleDateTimeAsISO = convertToISOString(schedule.scheduleDateTime);
  if (!scheduleDateTimeAsISO) {
    return null;
  }

  const timezoneIANA = TimezoneEnumToTimezoneMapping[schedule.timezone!];
  const shortTimezone = TimezoneEnumToShortTimezoneMappings[schedule.timezone!];
  const dateTime = DateTime.fromISO(scheduleDateTimeAsISO).setZone(timezoneIANA);
  return `${dateTime.toFormat("D 'from' h a")} ${shortTimezone}`;
};
